import { useEffect } from "react";
import type { CubemapPyramid } from "@heroncendre/schema";
import {
  CUBE_FACES,
  buildTileUrl,
  selectTileDelivery,
  supportsAvif,
  usePanoramaProgress,
  useTourState,
} from "@heroncendre/player";

/**
 * ============================================================================
 *  usePrechargementVersions.ts — Les autres ambiances de la scène, en avance
 * ----------------------------------------------------------------------------
 *  Le moteur réchauffe les scènes VOISINES, pas les autres versions de la scène
 *  regardée. Or un clic sur « Nuit » doit fondre vite : on réchauffe donc, une
 *  fois la scène affichée, le niveau de base de chacune de ses autres versions.
 *
 *  Même parti que le préchargement du moteur : le CACHE HTTP seulement (aucune
 *  texture créée), le niveau 0 seulement (six tuiles par version), différé et
 *  annulé si l'on quitte la scène — il ne doit jamais disputer la bande
 *  passante aux tuiles qu'on regarde.
 * ==========================================================================*/

const DELAI_MS = 600;
const PARALLELE = 4;

export function usePrechargementVersions(assetBaseUrl: string): void {
  const sceneId = useTourState((s) => s.currentSceneId);
  const tour = useTourState((s) => s.tour);
  const affichee = usePanoramaProgress().status === "shown";

  useEffect(() => {
    if (!affichee) return;
    const scene = tour.scenes.find((candidate) => candidate.id === sceneId);
    const pyramides = (scene?.visuals ?? []).flatMap((visual) =>
      visual.version.source.type === "cubemap" ? [visual.version.source.pyramid] : [],
    );
    if (pyramides.length < 2) return;

    const arret = new AbortController();
    const minuteur = window.setTimeout(() => {
      void (async () => {
        const avif = await supportsAvif();
        if (arret.signal.aborted) return;
        // La version affichée est déjà en cache : la redemander ne coûte qu'une
        // validation, plus simple que de deviner laquelle c'est.
        await toutCharger(pyramides.flatMap((pyramide) => tuilesDeBase(pyramide, assetBaseUrl, avif)), arret.signal);
      })();
    }, DELAI_MS);

    return () => {
      window.clearTimeout(minuteur);
      arret.abort();
    };
  }, [affichee, sceneId, tour, assetBaseUrl]);
}

function tuilesDeBase(pyramide: CubemapPyramid, assetBaseUrl: string, avif: boolean): string[] {
  const { urlTemplate } = selectTileDelivery(pyramide, avif);
  const cote = pyramide.levels[0]?.tileCount ?? 1;
  const urls: string[] = [];
  for (const face of CUBE_FACES) {
    for (let y = 0; y < cote; y++) {
      for (let x = 0; x < cote; x++) urls.push(buildTileUrl(assetBaseUrl, pyramide, face, 0, x, y, urlTemplate));
    }
  }
  return urls;
}

async function toutCharger(urls: readonly string[], signal: AbortSignal): Promise<void> {
  let curseur = 0;
  const ouvrier = async () => {
    while (curseur < urls.length && !signal.aborted) {
      const url = urls[curseur++]!;
      try {
        // Indication au navigateur : octets spéculatifs, après ce qu'on regarde.
        await fetch(url, { signal, priority: "low" } as RequestInit);
      } catch {
        // Abandon ou réseau : la tuile sera redemandée au clic, sans conséquence.
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLELE, urls.length) }, ouvrier));
}
